import { type NewDocumentType, usePostDocument } from 'entities/document/api';
import { useGetCurrentBoardId } from 'entities/user/api/hooks/useGetCurrentBoardId';
import { useTranslation } from 'react-i18next';
import { useShowNotification } from '../useShowNotification';

/**
 * Hook for creating a new document.
 */
export const useDocumentCreate = () => {
  const { t } = useTranslation('documents');
  const showNotification = useShowNotification();
  const currentBoardId = useGetCurrentBoardId();

  /**
   * Hook to create a new document.
   * It returns a promise that resolves with the final Batch once processing is complete.
   */
  const { mutateAsync: createMutation } = usePostDocument();

  /**
   * Default values for a new document.
   */
  const defaultDocument = {
    canEdit: false,
    contractValues: {},
    reference: '',
    withDefaultQueue: true,
  };

  /**
   * Creates a new document with the provided data and waits until the batch is finished.
   * @param documents - The data to create the document with.
   * @param boardId - The ID of the board to create the document in.
   */
  const create = async (
    documents: NewDocumentType | NewDocumentType[],
    boardId?: string | number,
  ) => {
    const localBoardId = boardId ?? currentBoardId;
    const documentsArray = (
      Array.isArray(documents) ? documents : [documents]
    ).map(d => ({
      ...defaultDocument,
      ...d,
    }));

    const result = await createMutation({
      documents: documentsArray,
      boardId: localBoardId!,
    });

    const newDocuments =
      result?.response?.type === 'CREATED_DOCUMENTS'
        ? result.response.documents
        : [];

    if (newDocuments.length) {
      if (newDocuments.length === 1) {
        showNotification({
          message: t('documents:notification.createdDocument', {
            name: newDocuments[0].name,
          }),
        });
      } else {
        showNotification({
          message: t('documents:notification.createdDocuments', {
            count: newDocuments.length,
          }),
        });
      }
    }

    return newDocuments;
  };

  return {
    create,
  };
};
